import React, { FC, ChangeEventHandler, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import { CheckIcon } from '@stpkg/stand-react';

import {
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { RSCheckBoxWithLabel } from 'src/reservation-page/RSCheckBoxWithLabel';

import { PaymentElementForm } from 'src/templates/merchants/purchase/components/PaymentElementForm';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { TokenResponse } from '@mul-pay/mptoken-js';
import { MulpayFormWrapper } from 'src/templates/merchants/resources/book/components/MulpayForm/MulpayFormWrapper';
export interface NewCreditCardItemProps {
  value?:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
  onChange: (
    paymentMethod: ReservationFlowSelectedPaymentMethodNewCreditCard,
  ) => void;
  onBlur: () => void;
  isMonthlyBilling: boolean;
  handlePaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
  isMulpayOrderEnabled?: boolean;
  handleMulpayTokenChange: (tokenResponse: TokenResponse) => void;
  merchantPublicId: string;
}

export const NewCreditCardItem: FC<NewCreditCardItemProps> = ({
  value,
  onChange,
  onBlur,
  isMonthlyBilling,
  handlePaymentElementChange,
  isMulpayOrderEnabled,
  handleMulpayTokenChange,
  merchantPublicId,
}) => {
  const intl = useIntl();
  const { isFocusVisible, focusProps } = useFocusRing();

  const color = '#0066FF';
  const colorTint = '#F1F7FF';

  const isSelected = useMemo(() => {
    if (typeof value === 'undefined') {
      return false;
    }

    return value.type === 'new_credit_card';
  }, [value]);

  const headerButtonRingSizeClasses = useMemo(() => {
    if (isFocusVisible) {
      return ['ring-2'];
    }

    if (isSelected) {
      return ['ring-1'];
    }

    return [];
  }, [isSelected, isFocusVisible]);

  const onHeaderButtonClick = useCallback(() => {
    if (typeof value !== 'undefined' && value.type === 'new_credit_card')
      return;

    onChange({
      type: 'new_credit_card',
      creditCardInfo: value?.creditCardInfo ?? {},
      isStoreCreditCard: false,
    });
    onBlur();
  }, [value, onChange, onBlur]);

  const onIsStoreCreditCardInputChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >(
    (event) => {
      if (isMonthlyBilling) return;

      if (typeof value === 'undefined' || value.type !== 'new_credit_card') {
        onChange({
          type: 'new_credit_card',
          creditCardInfo: {},
          isStoreCreditCard: event.target.checked,
        });
        return;
      }

      onChange({ ...value, isStoreCreditCard: event.target.checked });
    },
    [onChange, value, isMonthlyBilling],
  );

  return (
    <>
      <li
        className={classnames(
          'group',
          'list-style-none',
          'box-border',
          'block',
          'relative',
          'border-b',
          'border-t-0',
          'border-r-0',
          'border-l-0',
          'border-solid',
          'border-gray-200',
          'first-of-type:rounded-t-10',
          'last-of-type:rounded-b-10',
          'last-of-type:border-b-0',
        )}
      >
        <button
          {...mergeProps(focusProps, { onClick: onHeaderButtonClick })}
          type="button"
          className={classnames(
            'header-button',
            isSelected && 'selected',
            isFocusVisible && 'focus-visible',
            'appearance-none',
            'box-border',
            'relative',
            'block',
            'w-full',
            'p-2.5',
            'bg-transparent',
            'border-none',
            'outline-none',
            headerButtonRingSizeClasses,
            (isSelected || isFocusVisible) && 'z-1',
            'transition',
            'cursor-pointer',
            'group-first-of-type:rounded-t-10',
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex w-full items-center justify-between">
              <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                {intl.formatMessage({
                  defaultMessage: 'クレジットカード決済',
                  id: 'GnFQM8',
                  description:
                    'src/organisms/product-purchase/PaymentMethodInput/components/NewCreditCardItem/index.tsx',
                })}
              </div>
            </div>
            <div className="ml-1.5 flex-none">
              <div className="block h-[20px] w-[20px]">
                <span
                  className={classnames(
                    'mark',
                    'box-border',
                    'flex',
                    'justify-center',
                    'items-center',
                    'w-full',
                    'h-full',
                    'bg-white',
                    'border-gray-200',
                    'border-2',
                    'border-solid',
                    'rounded-full',
                    'transition',
                  )}
                >
                  <div className="icon-wrapper invisible h-16 w-16 transition">
                    <CheckIcon
                      className="block fill-white"
                      width="16"
                      height="16"
                      viewBox="0 0 48 48"
                    />
                  </div>
                </span>
              </div>
            </div>
          </div>
        </button>
        {/* トランジッションの検討が必要 */}
        <div
          className={classnames(
            'bg-gray-10',
            'px-2.5',
            'py-3',
            'border-t',
            'border-solid',
            'border-gray-200',
            'border-r-0',
            'border-l-0',
            'border-b-0',
            'group-last-of-type:rounded-b-10',
          )}
        >
          {isMulpayOrderEnabled ? (
            <MulpayFormWrapper
              // @ts-expect-error strictFunctionTypes
              onChange={handleMulpayTokenChange}
              merchantPublicId={merchantPublicId}
            />
          ) : (
            <PaymentElementForm
              onPaymentElementChange={handlePaymentElementChange}
            />
          )}

          <div className="mt-2">
            {(() => {
              if (isMonthlyBilling) {
                return (
                  <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                    {intl.formatMessage({
                      defaultMessage:
                        '毎月支払いが発生するため、カード情報は自動的に保存されます。',
                      id: 'C2WOIx',
                      description:
                        'src/organisms/product-purchase/PaymentMethodInput/components/NewCreditCardItem/index.tsx',
                    })}
                  </div>
                );
              }

              return (
                <RSCheckBoxWithLabel
                  labelText={intl.formatMessage({
                    defaultMessage: '登録済みのカード情報を上書きする',
                    id: 'LTkyCV',
                    description:
                      'src/organisms/product-purchase/PaymentMethodInput/components/NewCreditCardItem/index.tsx',
                  })}
                  checked={(() => {
                    if (typeof value === 'undefined') {
                      return false;
                    }

                    if (value.type !== 'new_credit_card') {
                      return false;
                    }

                    return value.isStoreCreditCard;
                  })()}
                  onChange={onIsStoreCreditCardInputChange}
                />
              );
            })()}
          </div>
        </div>
      </li>
      <style jsx>{`
        .header-button:hover {
          background-color: ${colorTint};
        }

        .header-button.focus-visible {
          --tw-ring-color: ${color};
        }

        .header-button.selected {
          --tw-ring-color: ${color};
          background-color: ${colorTint};
        }

        .header-button.selected .mark {
          background-color: ${color};
          border-color: ${color};
        }

        .header-button.selected .mark .icon-wrapper {
          visibility: visible;
        }
      `}</style>
    </>
  );
};
